import { Share2, Users } from 'lucide-react'
import type { LoadedModel } from '@viewer/store/viewerSlice'
import { useAuth } from '@core/auth/useAuth'
import { abrirJanelaPartilhar } from '@core/partilhas/partilhasEstado'
import {
  abrirModeloPartilhado,
  partilharModelo,
  recarregarModelos,
  type EstadoModelos,
} from './modelosPartilhados'
import { encontrarPorSha, revisaoMaisRecente } from './modelosRegras'

/** Na linha de cada modelo aberto: se está partilhado (e em que
 *  revisão), se há uma revisão mais recente, ou o botão para o
 *  partilhar. Não partilhar é o caso por omissão: nada sai da máquina
 *  sem se carregar aqui (ou no aviso que aparece ao abrir).
 *
 *  Partilhado, o rótulo "partilhado · rev. N" e o botão "Gerir partilha"
 *  abrem a janela "Partilhar com...": com quem está, acrescentar e
 *  remover pessoas, e "Retirar a partilha" (antes só se chegava lá pelo
 *  ícone em "Modelos do workspace"). */
export default function EstadoPartilhaDoModelo({
  model,
  estado,
}: {
  model: LoadedModel
  estado: EstadoModelos
}) {
  const { workspace } = useAuth()
  if (!estado.estado?.ativo || !estado.lista) return null
  const op = estado.operacoes[model.sha256]
  if (op) {
    return (
      <div className="mt-0.5 text-[10px] text-[var(--color-accent)]">
        {op.tipo === 'envio' ? 'A partilhar' : 'A descarregar'}… {op.feito}/{op.total}
      </div>
    )
  }
  const partilhado = encontrarPorSha(estado.lista, model.sha256)
  if (!partilhado) {
    if (model.sourceFormat === 'frag' || model.rawIfcAvailable === false) return null
    return (
      <button
        type="button"
        onClick={() => void partilharModelo(model)}
        title="Partilhar com o workspace e/ou pessoas: o ficheiro fica no servidor da plataforma e quem escolher pode abri-lo. Sem isto, fica só na sua máquina."
        className="mt-0.5 flex items-center gap-1 text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-accent)]"
      >
        <Share2 size={10} /> Partilhar com…
      </button>
    )
  }
  const nova = revisaoMaisRecente(estado.lista, model.sha256)
  const partilhaId = partilhado.partilha_id ?? null
  const gerir = () => {
    if (!partilhaId) return
    abrirJanelaPartilhar({
      tipo: 'modelo',
      nome: partilhado.nome,
      partilhaId,
      workspaceNome: workspace?.name,
      aoMudar: () => void recarregarModelos(),
    })
  }
  const quem = `Partilhado por ${partilhado.carregado_por_nome ?? partilhado.carregado_por_email ?? 'alguém'}. ${partilhado.visivel_para ?? ''}`
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10px]">
      {partilhaId ? (
        <button
          type="button"
          onClick={gerir}
          className="rounded bg-[var(--color-panel-elevated)] px-1 text-[var(--color-text-dim)] hover:text-[var(--color-accent)]"
          title={`${quem} Clique para ver com quem está partilhado.`}
        >
          partilhado · rev. {partilhado.revisao}
        </button>
      ) : (
        <span className="rounded bg-[var(--color-panel-elevated)] px-1 text-[var(--color-text-dim)]" title={quem}>
          partilhado · rev. {partilhado.revisao}
        </span>
      )}
      {partilhaId && (
        <button
          type="button"
          onClick={gerir}
          className="flex items-center gap-0.5 rounded border border-[var(--color-border)] px-1 text-[var(--color-text-muted)] hover:text-[var(--color-accent)]"
          title={
            partilhado.pode_gerir
              ? 'Com quem está partilhado este modelo (todas as revisões): acrescentar ou remover pessoas, ou retirar a partilha.'
              : 'Quem tem acesso a este modelo.'
          }
        >
          <Users size={9} aria-hidden />
          {partilhado.pode_gerir ? 'Gerir partilha' : 'Acessos'}
        </button>
      )}
      {nova && (
        <button
          type="button"
          onClick={() => void abrirModeloPartilhado(nova)}
          title={`Há uma revisão mais recente deste modelo no workspace (revisão ${nova.revisao}). Abre-a ao lado desta; remova a anterior quando quiser.`}
          className="rounded border border-[var(--color-warn-border)] px-1 text-[var(--color-warn-text)] hover:bg-[var(--color-accent-bg)]"
        >
          revisão {nova.revisao} disponível: abrir
        </button>
      )}
    </div>
  )
}
